// jsErrorLogger.js
import { bwBaseOrigin } from './baseUrl.js';

const ORIGIN = bwBaseOrigin();
const ENDPOINT = ORIGIN + '/log-js-error';

// One broken page (e.g. a webview looping on an error) must not flood the
// endpoint — hard cap per page load; the server throttles again on its side.
const MAX_EVENTS_PER_PAGE = 10;
let sentCount = 0;

// Seconds since the page HTML was rendered (bwData.pageServedAt, unix time).
// Attached to every event so a report self-classifies: large age = stale tab
// restored after hours/days, small age = fresh-page problem worth digging into,
// null = HTML predating this field.
function pageAgeSeconds() {
    try {
        const served = window.bwData?.pageServedAt;
        return served ? Math.round(Date.now() / 1000 - served) : null;
    } catch (e) {
        return null;
    }
}

function sendErrorLog(errorData) {
    if (sentCount >= MAX_EVENTS_PER_PAGE) return;
    sentCount++;
    errorData.page_age = pageAgeSeconds();
    try {
        fetch(ENDPOINT, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify(errorData),
        }).catch(function () {
            // A rejected telemetry POST (network blip) must die HERE. Without
            // this .catch it became an unhandled rejection, which our own
            // rejection listener then dutifully reported — the logger echoing
            // itself (field case: elegantstudio, 2026-09-01).
        });
    } catch (e) {
        // Silently fail (avoid logging errors from this logger itself)
    }
}

/**
 * Explicit fire-and-forget client report (used by confirm.js to report
 * storeAppointment failures with their HTTP status — those never reach
 * window.onerror because api.js catches them).
 */
export function logClientEvent({ message, status = null, step = null, context = null }) {
    sendErrorLog({
        message: message || 'Client event',
        file: null,
        line: null,
        column: null,
        stack: null,
        status,
        step,
        context,
        url: typeof location !== 'undefined' ? location.href : null,
        user_agent: navigator.userAgent,
    });
}

/**
 * Un rând care spune CE a picat, pentru mailurile cu status null / 200 care până acum veneau
 * cu context "null" (3 „căderi de rețea” în 24 h pe 9-10 sept 2026, fără nicio cauză vizibilă):
 *  - `TypeError: Failed to fetch | online=true | hidden=false`  → rețea / tab închis / extensie
 *  - `parse: Unexpected token < | <corpul răspunsului>`         → 200 cu corp murdar (api.js)
 *  - altfel corpul răspunsului, tăiat la 300 de caractere.
 */
export function describeError(err) {
    const env = ` | online=${typeof navigator !== 'undefined' && 'onLine' in navigator ? navigator.onLine : '?'}`
        + ` | hidden=${typeof document !== 'undefined' ? document.hidden : '?'}`;
    if (err instanceof Error) {
        return `${err.name}: ${err.message}`.slice(0, 200) + env;
    }
    const body = typeof err?.data === 'string' ? err.data : JSON.stringify(err?.data ?? null);
    const head = err?.parseError ? `parse: ${err.parseError} | ` : '';
    return (head + (body ?? 'null')).slice(0, 300);
}

// Ours = any of our bundles (min or not — the old '.min.js'-only filter
// dropped most real errors), or a page served from our own origin (hosted
// booking page: everything running there is ours). On embedded customer
// pages the bundle filter stays so we don't log their scripts.
function isOurs(stack, filename) {
    if (stack && stack.includes('booking-widget')) return true;
    if (filename && filename.includes('booking-widget')) return true;
    try {
        return location.origin === ORIGIN;
    } catch (e) {
        return false;
    }
}

(function () {
    function extractErrorInfo({ message, filename, lineno, colno, error }) {
        const stack = error?.stack || '';
        if (!isOurs(stack, filename)) return;

        sendErrorLog({
            message: message || 'Script error',
            file: filename || null,
            line: lineno || null,
            column: colno || null,
            stack,
            user_agent: navigator.userAgent,
            url: typeof location !== 'undefined' ? location.href : null,
        });
    }

    function handleUnhandledRejection(event) {
        const reason = event.reason || {};
        const stack = reason.stack || '';

        if (!isOurs(stack, null)) return;

        sendErrorLog({
            message: reason.message || 'Unhandled promise rejection',
            file: null,
            line: null,
            column: null,
            stack,
            user_agent: navigator.userAgent,
            url: typeof location !== 'undefined' ? location.href : null,
        });
    }

    window.addEventListener('error', extractErrorInfo, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejection, true);
})();
